import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../../../components/Button';
import Input from '@/components/Input';
import OrganizationSettingsTabs from '../components/SettingsTabs';
import useFetchOrganization from '../api/useFetchOrganization';
import useUpdateOrganization from '../api/useUpdateOrganization';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

/** マイページ文言設定のバリデーションスキーマ（バックエンドの ENDPOINT_LABEL_SCHEMA に合わせる） */
const portalSettingsSchema = z.object({
  entry: z.string().min(1, '入力してください').max(5, '5文字以内で入力してください'),
  decline: z.string().min(1, '入力してください').max(5, '5文字以内で入力してください'),
  already_entered: z.string().min(1, '入力してください').max(5, '5文字以内で入力してください'),
  already_declined: z.string().min(1, '入力してください').max(5, '5文字以内で入力してください'),
  entry_confirmation_message: z
    .string()
    .min(1, '入力してください')
    .max(15, '15文字以内で入力してください'),
  decline_confirmation_message: z
    .string()
    .min(1, '入力してください')
    .max(15, '15文字以内で入力してください'),
  entry_confirmed: z.string().min(1, '入力してください').max(7, '7文字以内で入力してください'),
  decline_confirmed: z.string().min(1, '入力してください').max(7, '7文字以内で入力してください'),
});

type PortalSettingsFormValues = z.infer<typeof portalSettingsSchema>;

/** フォームフィールドの定義（ラベル・プレースホルダー・ヘルプテキスト） */
const BUTTON_LABEL_FIELDS: {
  key: keyof PortalSettingsFormValues;
  label: string;
  placeholder: string;
  helpText: string;
}[] = [
  {
    key: 'entry',
    label: '応募ボタンの表示文言',
    placeholder: '応募する',
    helpText: '5文字以内',
  },
  {
    key: 'decline',
    label: '辞退ボタンの表示文言',
    placeholder: '辞退',
    helpText: '5文字以内',
  },
  {
    key: 'already_entered',
    label: '応募済みボタンの表示文言',
    placeholder: '応募済み',
    helpText: '5文字以内',
  },
  {
    key: 'already_declined',
    label: '辞退済みボタンの表示文言',
    placeholder: '辞退済み',
    helpText: '5文字以内',
  },
];

const CONFIRMATION_MESSAGE_FIELDS: {
  key: keyof PortalSettingsFormValues;
  label: string;
  placeholder: string;
  helpText: string;
}[] = [
  {
    key: 'entry_confirmation_message',
    label: '応募確認メッセージ',
    placeholder: 'この求人に応募しますか？',
    helpText: '15文字以内',
  },
  {
    key: 'decline_confirmation_message',
    label: '辞退確認メッセージ',
    placeholder: 'この求人を辞退しますか？',
    helpText: '15文字以内',
  },
];

const CONFIRMED_BUTTON_FIELDS: {
  key: keyof PortalSettingsFormValues;
  label: string;
  placeholder: string;
  helpText: string;
}[] = [
  {
    key: 'entry_confirmed',
    label: '応募確定ボタンの表示文言',
    placeholder: '応募を確定する',
    helpText: '7文字以内',
  },
  {
    key: 'decline_confirmed',
    label: '辞退確定ボタンの表示文言',
    placeholder: '辞退を確定する',
    helpText: '7文字以内',
  },
];

export default function PortalSettings() {
  const { data: organization, refetch: refetchOrganization } = useFetchOrganization();
  const { mutateAsync: updateOrganization } = useUpdateOrganization();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PortalSettingsFormValues>({
    resolver: zodResolver(portalSettingsSchema),
    defaultValues: {
      entry: '',
      decline: '',
      already_entered: '',
      already_declined: '',
      entry_confirmation_message: '',
      decline_confirmation_message: '',
      entry_confirmed: '',
      decline_confirmed: '',
    },
  });

  // APIから取得した現在値をフォームに反映する
  useEffect(() => {
    if (organization?.endpoint_label_setting) {
      const setting = organization.endpoint_label_setting;
      reset({
        entry: setting.entry,
        decline: setting.decline,
        already_entered: setting.already_entered,
        already_declined: setting.already_declined,
        entry_confirmation_message: setting.entry_confirmation_message,
        decline_confirmation_message: setting.decline_confirmation_message,
        entry_confirmed: setting.entry_confirmed,
        decline_confirmed: setting.decline_confirmed,
      });
    }
  }, [organization, reset]);

  const onSubmit = async (values: PortalSettingsFormValues) => {
    try {
      await updateOrganization({ endpoint_label_settings: values });
      await refetchOrganization();
      showSuccessToast('マイページ設定を保存しました。');
    } catch {
      showErrorToast('マイページ設定を保存できませんでした。');
    }
  };

  const renderField = ({
    key,
    label,
    placeholder,
    helpText,
  }: {
    key: keyof PortalSettingsFormValues;
    label: string;
    placeholder: string;
    helpText: string;
  }) => (
    <div key={key} className="mb-6">
      <label htmlFor={key} className="block text-body-sm font-medium">
        {label}
      </label>
      <Input
        id={key}
        type="text"
        placeholder={placeholder}
        {...register(key)}
        className={`mt-2 w-full max-w-[400px] ${errors[key] ? 'border-red-500' : ''}`}
      />
      {errors[key] ? (
        <p className="mt-1 text-xs text-red-600">{errors[key]?.message}</p>
      ) : (
        <p className="mt-1 text-xs text-sub">{helpText}</p>
      )}
    </div>
  );

  return (
    <>
      <OrganizationSettingsTabs />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="mt-4 rounded-lg bg-white px-12 py-11">
          <h2 className="mb-6 text-base font-bold">ボタン文言</h2>
          {BUTTON_LABEL_FIELDS.map(renderField)}
        </div>

        <div className="mt-4 rounded-lg bg-white px-12 py-11">
          <h2 className="mb-6 text-base font-bold">確認メッセージ</h2>
          {CONFIRMATION_MESSAGE_FIELDS.map(renderField)}
        </div>

        <div className="mt-4 rounded-lg bg-white px-12 py-11">
          <h2 className="mb-6 text-base font-bold">確定ボタン文言</h2>
          {CONFIRMED_BUTTON_FIELDS.map(renderField)}
        </div>

        <Button
          type="submit"
          color="primary"
          disabled={isSubmitting}
          className="fixed right-6 bottom-6 h-[52px] w-[96px] text-base"
        >
          {isSubmitting ? '保存中...' : '保存する'}
        </Button>
      </form>
    </>
  );
}
